import { useCallback, useLayoutEffect, useRef, useState } from 'react';

function expandedBounds(node) {
    const workspace = node.closest('.portal-workspace')?.getBoundingClientRect();
    const header = node.closest('.portal-workspace')?.querySelector('.portal-topbar')?.getBoundingClientRect();
    const gutter = window.innerWidth < 768 ? 12 : 24;
    const left = Math.max(gutter, (workspace?.left || 0) + gutter);
    const top = Math.max(gutter, Math.min(header?.bottom || 0, window.innerHeight / 4) + 16);
    return { left, top, width: Math.max(0, window.innerWidth - left - gutter), height: Math.max(0, window.innerHeight - top - gutter) };
}

function originTransform(origin, destination) {
    return `translate(${origin.left - destination.left}px, ${origin.top - destination.top}px) scale(${origin.width / (destination.width || 1)}, ${origin.height / (destination.height || 1)})`;
}

export default function useReportLift(card, slot, onExpand) {
    const [phase, setPhase] = useState('closed');
    const [geometry, setGeometry] = useState(null);
    const animation = useRef(null);
    const closingTransform = useRef('none');
    const expanded = phase !== 'closed';

    const expand = () => {
        if (expanded || !card.current) return;
        const origin = card.current.getBoundingClientRect();
        setGeometry({ origin: { left: origin.left, top: origin.top, width: origin.width, height: origin.height }, destination: expandedBounds(card.current) });
        onExpand?.();
        setPhase('opening');
    };
    const restore = useCallback(() => {
        closingTransform.current = card.current ? window.getComputedStyle(card.current).transform : 'none';
        setPhase((current) => current === 'closed' ? current : 'closing');
    }, [card]);

    useLayoutEffect(() => {
        if (!geometry || phase === 'closed') return;
        const node = card.current;
        if (phase !== 'closing') {
            const previousHeight = node.style.height;
            node.style.height = 'auto';
            const contentHeight = Math.ceil(node.getBoundingClientRect().height);
            node.style.height = previousHeight;
            const height = Math.min(expandedBounds(node).height, Math.max(300, contentHeight));
            if (Math.abs(height - geometry.destination.height) > 1) {
                setGeometry((current) => ({ ...current, destination: { ...current.destination, height } }));
                return;
            }
            if (phase === 'open') return;
        }
        const closing = phase === 'closing';
        const finish = () => setPhase(closing ? 'closed' : 'open');
        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        if (reducedMotion || !node?.animate) { finish(); return; }
        const origin = closing ? slot.current.getBoundingClientRect() : geometry.origin;
        const transform = originTransform(origin, geometry.destination);
        const frames = closing
            ? [{ transform: closingTransform.current }, { transform }]
            : [{ transform }, { transform: 'none' }];
        const motion = node.animate(frames, { duration: closing ? 220 : 300, easing: closing ? 'cubic-bezier(.4,0,.6,1)' : 'cubic-bezier(.2,.8,.2,1)', fill: 'both' });
        animation.current = motion;
        let cancelled = false;
        motion.finished.then(() => { if (!cancelled) finish(); }).catch(() => {});
        return () => { cancelled = true; motion.cancel(); animation.current = null; };
    }, [phase, geometry, card, slot]);

    useLayoutEffect(() => {
        if (!expanded) return;
        const resize = () => {
            // Finish the transition at the new viewport size; never leave a card off screen.
            animation.current?.cancel();
            setGeometry((current) => ({ ...current, destination: expandedBounds(card.current) }));
            setPhase((current) => current === 'closing' ? 'closed' : 'open');
        };
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, [expanded, card]);

    return { phase, expanded, expand, restore, geometry };
}
